import React from "react";

// =====================================================================
// Lawyers Directory Rebuild V2 — verbatim artboard icon kit (PKG-0).
//
// Brief RULE 2 (every symbol verbatim, zero tolerance): each component
// below copies its SVG path data 1:1 from REBUILDV2/SPEC-SYMBOLS.md
// (source: handoff "TransferPitch Lawyers.dc.html"). Never alter path
// data, never swap for a merely-similar library icon.
//
// All glyphs use viewBox="0 0 24 24" fill="none", stroke-based.
// Color policy per SPEC-SYMBOLS:
//   - Icons the handoff draws in MULTIPLE colors (search, chevron down,
//     checkmark, close X) take `stroke="currentColor"` — the call site
//     sets the exact artboard color via text-color class or `style`.
//   - Icons the handoff draws in exactly ONE color everywhere keep that
//     color fixed as the default (still overridable via `color` where a
//     surface might need it, but the spec default is baked in).
// `size` / `strokeWidth` defaults match the most common instance; the
// per-instance sizes are listed in each icon's comment.
//
// Flags are NOT here — emoji flags are placeholders (brief RULE 3); use
// the platform image-flag components per REUSE-MAP section 1.
// =====================================================================

// SPEC-SYMBOLS #1 — Search icon (magnifier).
// Instances: directory/mobile search 14×14 #98A2B3 w2; empty-state hero
// 34×34 #8FB6EC w2; sheet country search 12×12 #98A2B3 w2. Multi-color
// → currentColor.
export const SearchIcon = ({ size = 14, strokeWidth = 2, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    className={`flex-none ${className}`}
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7"></circle>
    <path d="M21 21l-4.3-4.3"></path>
  </svg>
);

// SPEC-SYMBOLS #2 — Chevron down.
// Instances (all w2.5 round): active filter 12×12 #1567D2; inactive
// filters/selects/collapsed accordions 12×12 #98A2B3; mobile active
// country pill 11×11 #1567D2. Multi-color → currentColor.
export const ChevronDownIcon = ({ size = 12, strokeWidth = 2.5, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    className={`flex-none ${className}`}
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6"></path>
  </svg>
);

// SPEC-SYMBOLS #3 — Chevron up (expanded accordion).
// Single instance: mobile filter-sheet country header 12×12 #667085 w2.5
// round. Single-color → fixed #667085 default.
export const ChevronUpIcon = ({ size = 12, strokeWidth = 2.5, color = "#667085", className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    className={`flex-none ${className}`}
    aria-hidden="true"
  >
    <path d="M18 15l-6-6-6 6"></path>
  </svg>
);

// SPEC-SYMBOLS #4 — Checkmark.
// Instances: verified badge 11×11 #1567D2 w3 (mobile 10×10); success
// modal 26×26 #12B76A w2.5; license verified tick 13×13 #12B76A w2.5;
// mobile checkbox 11×11 #ffffff w3.5. All round+join. Multi-color →
// currentColor; pass the instance's size/strokeWidth.
export const CheckmarkIcon = ({ size = 11, strokeWidth = 3, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`flex-none ${className}`}
    aria-hidden="true"
  >
    <path d="M20 6L9 17l-5-5"></path>
  </svg>
);

// SPEC-SYMBOLS #5 — Close X.
// Instances (all round): filter-chip remove 11×11 #1567D2 w2.5; side
// panel close 16×16 #667085 w2.5; contact modal close 15×15 #667085
// w2.5. Multi-color → currentColor.
export const CloseIcon = ({ size = 15, strokeWidth = 2.5, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    className={`flex-none ${className}`}
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12"></path>
  </svg>
);

// SPEC-SYMBOLS #6 — Chat bubble (Contact Lawyer buttons).
// All instances #ffffff w2 round+join: card/mobile-card button 14×14;
// detail panel + mobile sticky bar 15×15. Single-color → fixed #ffffff.
export const ChatBubbleIcon = ({ size = 14, color = "#ffffff", className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`flex-none ${className}`}
    aria-hidden="true"
  >
    <path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5c-1.5 0-2.9-.38-4.1-1.05L3 20l1.05-5.4A8.5 8.5 0 1 1 21 11.5z"></path>
  </svg>
);

// SPEC-SYMBOLS #7 — Email envelope.
// All instances 12×12 #98A2B3 w2, NO linecap attr (per spec): card
// contact rows, detail panel, mobile detail. Single-color → fixed.
export const EnvelopeIcon = ({ size = 12, color = "#98A2B3", className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    className={`flex-none ${className}`}
    aria-hidden="true"
  >
    <rect x="2" y="4" width="20" height="16" rx="2"></rect>
    <path d="M2 7l10 6 10-6"></path>
  </svg>
);

// SPEC-SYMBOLS #8 — Phone handset.
// All instances 12×12 #98A2B3 w2 round+join: card contact rows, detail
// panel, mobile detail. Single-color → fixed.
export const PhoneIcon = ({ size = 12, color = "#98A2B3", className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`flex-none ${className}`}
    aria-hidden="true"
  >
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.13.96.36 1.9.7 2.8a2 2 0 0 1-.45 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.27a2 2 0 0 1 2.1-.45c.9.34 1.84.57 2.8.7A2 2 0 0 1 22 16.9z"></path>
  </svg>
);

// SPEC-SYMBOLS #9 — Shield-check (license / trust box).
// Instances #1567D2 w2 round+join: detail panel 17×17, mobile 16×16.
// Single-color → fixed #1567D2.
export const ShieldCheckIcon = ({ size = 17, color = "#1567D2", className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`flex-none ${className}`}
    aria-hidden="true"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    <path d="M9 12l2 2 4-4"></path>
  </svg>
);

// SPEC-SYMBOLS #10 — Bell (notifications, mobile directory header).
// Single instance: 17×17 #667085 w2 round. Single-color → fixed.
export const BellIcon = ({ size = 17, color = "#667085", className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    className={`flex-none ${className}`}
    aria-hidden="true"
  >
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
    <path d="M13.7 21a2 2 0 0 1-3.4 0"></path>
  </svg>
);

// SPEC-SYMBOLS #11 — Back arrow (mobile detail nav bar).
// Single instance: 17×17 #344054 w2.5 round+join. Single-color → fixed.
export const BackArrowIcon = ({ size = 17, color = "#344054", className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`flex-none ${className}`}
    aria-hidden="true"
  >
    <path d="M19 12H5M12 19l-7-7 7-7"></path>
  </svg>
);

const lawyerV2Icons = {
  SearchIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckmarkIcon,
  CloseIcon,
  ChatBubbleIcon,
  EnvelopeIcon,
  PhoneIcon,
  ShieldCheckIcon,
  BellIcon,
  BackArrowIcon,
};

export default lawyerV2Icons;
